import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ContentHeader from "src/widgets/contentHeader/ContentHeader";
import LoaderWrapper from "src/widgets/loaderWrapper/LoaderWrapper";
import {
    useGetSupportTicketByIdQuery,
    useAddSupportTicketMessageMutation,
    useMarkSupportTicketMessagesReadMutation,
    useGetUnreadTicketStatsQuery,
    useCheckSupportModuleStatusQuery,
} from "src/api/rtk/indorsoftRu/slices/supportApiSlice";
import { useGetBitrixSessionIdQuery } from "src/api/rtk/indorsoftRu/slices/bitrixSessionApiSlice";
import { useCheckIndorIDAuthQuery } from "src/api/rtk/indorsoftRu/slices/indorIDAuthApiSlice";
import css from "./SupportTicketPage.module.scss";
import TicketSummary from "./components/TicketSummary";
import MessagesList from "./components/MessagesList";
import MessageForm from "./components/MessageForm";
import { sortMessagesByDate, groupMessagesByDate } from "./utils/supportTicketUtils";
import { useSupportTicketScroll } from "./hooks/useSupportTicketScroll";
import { useMessageReadTracking } from "./hooks/useMessageReadTracking";
import { getItemWebStorage } from "src/features/webStorageSaver";

export default function SupportTicketPage() {
    const { ticketId } = useParams();
    const navigate = useNavigate();
    const [sendError, setSendError] = useState<string | null>(null);
    const messagesListRef = useRef<HTMLUListElement | null>(null);
    const newMessagesLabelRef = useRef<HTMLLIElement | null>(null);
    const markMessagesAboveAsSeenRef = useRef<(() => void) | null>(null);

    const currentConfig = getItemWebStorage("currentConfig") ?? {};
    const isSupportEnabledInConfig = currentConfig?.support?.enabled ?? false;

    const { data: bitrixSessionData } = useGetBitrixSessionIdQuery(undefined, {
        skip: !isSupportEnabledInConfig,
    });

    // Проверка активности модуля поддержки
    const { data: supportModuleStatus, isLoading: isCheckingModuleStatus } = useCheckSupportModuleStatusQuery(undefined, {
        skip: !isSupportEnabledInConfig,
        refetchOnMountOrArgChange: true,
    });

    // Проверка авторизации и перезапрос статистики непрочитанных при выходе со страницы
    const { data: authData, isLoading: isCheckingAuth } = useCheckIndorIDAuthQuery(undefined, { skip: !isSupportEnabledInConfig });
    const { refetch: refetchUnreadStats } = useGetUnreadTicketStatsQuery(undefined, {
        skip: !isSupportEnabledInConfig || !authData?.authorized || !supportModuleStatus?.isActive,
    });

    // Редирект на страницу "Справка" при отсутствии авторизации
    useEffect(() => {
        // Ждем завершения проверки авторизации
        if (!isCheckingAuth && authData && !authData.authorized) {
            navigate('/app/help');
        }
    }, [authData, isCheckingAuth, navigate]);

    // Сохраняем refetch и authData в ref для использования в cleanup
    const refetchUnreadStatsRef = useRef(refetchUnreadStats);
    const authDataRef = useRef(authData);
    useEffect(() => {
        refetchUnreadStatsRef.current = refetchUnreadStats;
        authDataRef.current = authData;
    }, [refetchUnreadStats, authData]);

    // Перезапрашиваем статистику непрочитанных при размонтировании (выходе со страницы диалога)
    useEffect(() => {
        return () => {
            // Cleanup функция выполнится при размонтировании компонента
            // Проверяем авторизацию и наличие refetch перед вызовом
            if (authDataRef.current?.authorized && refetchUnreadStatsRef.current) {
                try {
                    // Проверяем, что refetch является функцией перед вызовом
                    if (typeof refetchUnreadStatsRef.current === 'function') {
                        refetchUnreadStatsRef.current();
                    }
                } catch (error) {
                    // Игнорируем ошибки при размонтировании (например, если запрос не был запущен)
                    // Это нормально, если пользователь не был авторизован или запрос был пропущен
                }
            }
        };
    }, []); // Выполняем только при монтировании, cleanup - при размонтировании

    const {
        data,
        isLoading,
        isError,
        refetch,
    } = useGetSupportTicketByIdQuery(ticketId ?? "", {
        skip: !isSupportEnabledInConfig || !ticketId || !supportModuleStatus?.isActive,
        pollingInterval: 10000, // Polling каждые 10 секунд для обновления тикета
        refetchOnMountOrArgChange: true,
        refetchOnFocus: true,
        refetchOnReconnect: true,
    });

    const [addMessage, { isLoading: isSending }] = useAddSupportTicketMessageMutation();
    const [markSupportMessagesRead] = useMarkSupportTicketMessagesReadMutation();

    const ticket = data?.ticket;
    const messages = sortMessagesByDate(data?.messages);
    const messagesByDate = groupMessagesByDate(messages);

    // Сохраняем начальные значения при первом заходе на тикет, чтобы избежать мигания при поллинге
    const initialLastReadSupportIdRef = useRef<number | null>(null);
    const initialFirstNewSupportMessageIdRef = useRef<string | null>(null);
    const ticketIdRef = useRef<string | undefined>(ticketId);

    // Сбрасываем при изменении ticketId
    useEffect(() => {
        if (ticketIdRef.current !== ticketId) {
            ticketIdRef.current = ticketId;
            initialLastReadSupportIdRef.current = null;
            initialFirstNewSupportMessageIdRef.current = null;
        }
    }, [ticketId]);

    const currentLastReadSupportId = Number(ticket?.LAST_READ_SUPPORT_MESSAGE_ID) || 0;

    // Инициализируем значения только при первом заходе или изменении ticketId
    useEffect(() => {
        if (!ticket || !messages.length) return;
        
        // Если значения еще не инициализированы, сохраняем их
        if (initialLastReadSupportIdRef.current === null) {
            initialLastReadSupportIdRef.current = currentLastReadSupportId;
            
            // Находим ID первого нового сообщения поддержки
            let firstNewId: string | null = null;
            if (currentLastReadSupportId <= 0) {
                // Если ничего не прочитано, первое сообщение поддержки в диалоге - новое
                const firstSupport = messages.find((m) => m.MESSAGE_BY_SUPPORT_TEAM === "Y");
                firstNewId = firstSupport?.ID || null;
            } else {
                // Находим первое сообщение поддержки с ID > currentLastReadSupportId
                const firstNewSupport = messages.find((m) => {
                    if (m.MESSAGE_BY_SUPPORT_TEAM !== "Y") return false;
                    const msgId = Number(m.ID);
                    return Number.isFinite(msgId) && msgId > currentLastReadSupportId;
                });
                firstNewId = firstNewSupport?.ID || null;
            }
            initialFirstNewSupportMessageIdRef.current = firstNewId;
        }
    }, [ticket, messages, currentLastReadSupportId]);

    // Используем сохраненные значения для стабильности при поллинге
    const lastReadSupportId = initialLastReadSupportIdRef.current ?? currentLastReadSupportId;
    
    // Вычисляем firstNewSupportMessageId, если он еще не был сохранен
    const firstNewSupportMessageId = initialFirstNewSupportMessageIdRef.current ?? (() => {
        if (lastReadSupportId <= 0) {
            const firstSupport = messages.find((m) => m.MESSAGE_BY_SUPPORT_TEAM === "Y");
            return firstSupport?.ID || null;
        }
        const firstNewSupport = messages.find((m) => {
            if (m.MESSAGE_BY_SUPPORT_TEAM !== "Y") return false;
            const msgId = Number(m.ID);
            return Number.isFinite(msgId) && msgId > lastReadSupportId;
        });
        return firstNewSupport?.ID || null;
    })();

    // Подсчитываем количество новых сообщений поддержки на основе сохраненного lastReadSupportId
    const newMessagesCount = (() => {
        if (lastReadSupportId <= 0) {
            // Если ничего не прочитано, все сообщения поддержки - новые
            return messages.filter((m) => m.MESSAGE_BY_SUPPORT_TEAM === "Y").length;
        }
        // Подсчитываем сообщения поддержки с ID > lastReadSupportId
        return messages.filter((m) => {
            if (m.MESSAGE_BY_SUPPORT_TEAM !== "Y") return false;
            const msgId = Number(m.ID);
            return Number.isFinite(msgId) && msgId > lastReadSupportId;
        }).length;
    })();

    // Используем хук для логики скролла
    useSupportTicketScroll({
        messages,
        lastReadSupportId,
        ticketId,
        messagesListRef,
    });

    // Используем хук для отслеживания прочитанных сообщений
    const { maxSeenMessageIdRef } = useMessageReadTracking({
        ticketId,
        messages,
        bitrixSessionId: bitrixSessionData?.sessid,
        messagesListRef,
    });

    const title = ticketId ? `Обращение №${ticketId}` : "Обращение";
    const isClosed = Boolean(ticket?.DATE_CLOSE);

    async function handleSendMessage(message: string, files: File[]) {
        if (!ticketId) return;

        const sessid = bitrixSessionData?.sessid;
        if (!sessid) {
            setSendError("Не удалось получить сессию Bitrix. Попробуйте обновить страницу.");
            return;
        }

        try {
            setSendError(null);
            
            // Помечаем все сообщения поддержки до момента отправки как прочитанные
            const lastSupportMessage = messages
                .filter((m) => m.MESSAGE_BY_SUPPORT_TEAM === "Y")
                .sort((a, b) => Number(b.ID) - Number(a.ID))[0];
            
            if (lastSupportMessage) {
                const lastSupportId = Number(lastSupportMessage.ID);
                if (Number.isFinite(lastSupportId) && lastSupportId > maxSeenMessageIdRef.current) {
                    try {
                        await markSupportMessagesRead({
                            id: ticketId,
                            lastSeenMessageId: lastSupportId,
                            sessid,
                        }).unwrap();
                        maxSeenMessageIdRef.current = lastSupportId;
                    } catch {
                        // Не блокируем отправку сообщения, если не удалось пометить прочитанным
                    }
                }
            }
            
            await addMessage({
                id: ticketId,
                message,
                files,
                sessid,
            }).unwrap();
            
            await refetch();
            // Скроллим вниз после обновления сообщений
            requestAnimationFrame(() => {
                const list = messagesListRef.current;
                if (list) {
                    list.scrollTop = list.scrollHeight;
                    // Помечаем все сообщения поддержки выше текущей позиции как просмотренные
                    // Используем небольшую задержку, чтобы DOM успел обновиться после скролла
                    setTimeout(() => {
                        if (markMessagesAboveAsSeenRef.current) {
                            markMessagesAboveAsSeenRef.current();
                        }
                    }, 100);
                }
            });
        } catch {
            setSendError("Не удалось отправить сообщение. Попробуйте позже.");
            throw new Error("Failed to send message");
        }
    }

    // Если модуль не активен, показываем предупреждение
    if (!isSupportEnabledInConfig) {
        return (
            <>
                <ContentHeader title={title} />
                <div className={css.supportTicketPage}>
                    <div className={css.errorMessage}>
                        Модуль тех. поддержки отключён в конфигурации базы.
                    </div>
                </div>
            </>
        );
    }

    if (isCheckingModuleStatus) {
        return (
            <>
                <ContentHeader title={title} />
                <LoaderWrapper isLoading={true}>
                    <div></div>
                </LoaderWrapper>
            </>
        );
    }

    if (!supportModuleStatus?.isActive) {
        return (
            <>
                <ContentHeader title={title} />
                <div className={css.supportTicketPage}>
                    <div className={css.errorMessage}>
                        Служба тех. поддержки временно недоступна. Обратитесь к администратору системы.
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <ContentHeader title={title} />
            <LoaderWrapper isLoading={isLoading}>
                <div className={css.supportTicketPage}>
                    {isError || !ticketId ? (
                        <div className={css.errorMessage}>Ошибка загрузки обращения</div>
                    ) : !ticket ? (
                        <div className={css.errorMessage}>Обращение не найдено</div>
                    ) : (
                        <div className={css.content}>
                            <TicketSummary ticket={ticket} />

                            <section className={css.messagesSection}>
                                <h3 className={css.messagesTitle}>История диалога</h3>
                                {messages.length === 0 ? (
                                    <div className={css.emptyMessages}>Сообщений нет</div>
                                ) : (
                                    <MessagesList
                                        messagesByDate={messagesByDate}
                                        firstNewSupportMessageId={firstNewSupportMessageId}
                                        newMessagesCount={newMessagesCount}
                                        messagesListRef={messagesListRef}
                                        newMessagesLabelRef={newMessagesLabelRef}
                                        ticketId={ticketId}
                                        onMarkMessagesAboveAsSeenReady={(markFn) => {
                                            markMessagesAboveAsSeenRef.current = markFn;
                                        }}
                                    />
                                )}

                                {isClosed && (
                                    <div className={css.chatClosedNote}>
                                        Обращение закрыто
                                    </div>
                                )}

                                <MessageForm
                                    isClosed={isClosed}
                                    isSending={isSending}
                                    sendError={sendError}
                                    onSubmit={handleSendMessage}
                                />
                            </section>
                        </div>
                    )}
                </div>
            </LoaderWrapper>
        </>
    );
}
